/**
 * Furniture surface listing specs (Wave 2 furniture packet Phase 1).
 *
 * Owns: `table`, `center-table`, `office-table`, `tv-console`, `wardrobe`, `dresser`
 * — build / validate / compose + wardrobe↔dresser title inference for mapping/backfill.
 *
 * Dimensions (`length_ft`, `width_ft`, `height_ft`, `dimensions_ft`) are always optional.
 */

import {
  composeListingTitleFromSlotsWithIdentity,
  type ListingTitleIdentity,
} from "@/lib/listing/listingTitleTokens";
import { listingTitleMaterialWord } from "@/lib/listing/chairListingSpec";
import {
  DINING_TABLE_TOP_MATERIALS,
  formatDiningTableTopMaterialLabel,
  type DiningTableTopMaterial,
} from "@/lib/listing/diningTableChairListingSpec";
import { LEGACY_WARDROBE_DRESSER_SUBCATEGORY_ID } from "@/lib/categoryStructure";
import { asListingAttributesRecord } from "@/lib/listing/listingAttributesJson";
import type {
  ListingSpecAttributes,
  ListingSpecValidationIssue,
} from "@/lib/listing/specRequirements";

export { LEGACY_WARDROBE_DRESSER_SUBCATEGORY_ID };

/** Legacy combined picker id — re-exported for backfill/mapping callers. */
export type LegacyWardrobeDresserId =
  typeof LEGACY_WARDROBE_DRESSER_SUBCATEGORY_ID;

export const FURNITURE_SURFACE_SUBCATEGORY_IDS = [
  "table",
  "center-table",
  "office-table",
  "tv-console",
  "wardrobe",
  "dresser",
] as const;

export type FurnitureSurfaceSubcategoryId =
  (typeof FURNITURE_SURFACE_SUBCATEGORY_IDS)[number];

export const FURNITURE_MATERIALS = [
  "wood",
  "mdf",
  "metal",
  "glass",
  "other",
] as const;
export type FurnitureMaterial = (typeof FURNITURE_MATERIALS)[number];

export const LENGTH_FT_MIN = 1;
export const LENGTH_FT_MAX = 20;
export const WIDTH_FT_MIN = 1;
export const WIDTH_FT_MAX = 10;
export const HEIGHT_FT_MIN = 1;
export const HEIGHT_FT_MAX = 10;

export type FurnitureSurfaceSpecFormState = {
  tableTopMaterial: DiningTableTopMaterial | "";
  furnitureMaterial: FurnitureMaterial | "";
  lengthFt: number | "";
  widthFt: number | "";
  heightFt: number | "";
};

export function defaultFurnitureSurfaceSpecFormState(): FurnitureSurfaceSpecFormState {
  return {
    tableTopMaterial: "",
    furnitureMaterial: "",
    lengthFt: "",
    widthFt: "",
    heightFt: "",
  };
}

export function isFurnitureSurfaceSubcategory(
  subcategoryId: string | null | undefined,
): subcategoryId is FurnitureSurfaceSubcategoryId {
  return (
    typeof subcategoryId === "string" &&
    (FURNITURE_SURFACE_SUBCATEGORY_IDS as readonly string[]).includes(
      subcategoryId,
    )
  );
}

/** Materials shown for wardrobe / dresser (no glass — D4 / mockup). */
export const WARDROBE_DRESSER_MATERIALS = [
  "wood",
  "mdf",
  "metal",
  "other",
] as const satisfies readonly FurnitureMaterial[];

/** Optional desk material pills for `office-table`. */
export const OFFICE_TABLE_MATERIALS = [
  "wood",
  "metal",
  "glass",
  "other",
] as const satisfies readonly FurnitureMaterial[];

export type FurnitureSurfaceMaterialKind =
  | "table_top"
  | "furniture"
  | "furniture_optional"
  | "none";

export type FurnitureSurfaceUiProfile = {
  subcategoryId: FurnitureSurfaceSubcategoryId;
  sectionLabel: string;
  /** When false, section has no required asterisk (office-table). */
  showRequiredMarker: boolean;
  materialKind: FurnitureSurfaceMaterialKind;
  materialLabel: string;
  furnitureMaterialOptions: readonly FurnitureMaterial[];
  axes: { length: boolean; width: boolean; height: boolean };
  dimensionsSummaryLabel: string;
  /** Collapse material + dimensions behind one optional expander. */
  entirelyOptionalPanel: boolean;
};

export function getFurnitureSurfaceSpecSectionLabel(
  subcategoryId: FurnitureSurfaceSubcategoryId,
): string {
  switch (subcategoryId) {
    case "center-table":
      return "Center table details";
    case "office-table":
      return "Desk details";
    case "tv-console":
      return "TV console details";
    case "wardrobe":
      return "Wardrobe details";
    case "dresser":
      return "Dresser details";
    case "table":
    default:
      return "Table details";
  }
}

/** Config-driven Step 2 panel profile (Phase 3). */
export function getFurnitureSurfaceUiProfile(
  subcategoryId: FurnitureSurfaceSubcategoryId,
): FurnitureSurfaceUiProfile {
  const sectionLabel = getFurnitureSurfaceSpecSectionLabel(subcategoryId);
  switch (subcategoryId) {
    case "table":
    case "center-table":
      return {
        subcategoryId,
        sectionLabel,
        showRequiredMarker: true,
        materialKind: "table_top",
        materialLabel: "Table top material",
        furnitureMaterialOptions: FURNITURE_MATERIALS,
        axes: { length: true, width: true, height: false },
        dimensionsSummaryLabel: "Table size (optional)",
        entirelyOptionalPanel: false,
      };
    case "tv-console":
      return {
        subcategoryId,
        sectionLabel,
        showRequiredMarker: true,
        materialKind: "furniture",
        materialLabel: "TV console material",
        furnitureMaterialOptions: FURNITURE_MATERIALS,
        axes: { length: true, width: false, height: true },
        dimensionsSummaryLabel: "Size (optional)",
        entirelyOptionalPanel: false,
      };
    case "wardrobe":
    case "dresser":
      return {
        subcategoryId,
        sectionLabel,
        showRequiredMarker: true,
        materialKind: "furniture",
        materialLabel: "Primary material",
        furnitureMaterialOptions: WARDROBE_DRESSER_MATERIALS,
        axes: { length: true, width: true, height: true },
        dimensionsSummaryLabel: "Size (optional)",
        entirelyOptionalPanel: false,
      };
    case "office-table":
      return {
        subcategoryId,
        sectionLabel,
        showRequiredMarker: false,
        materialKind: "furniture_optional",
        materialLabel: "Material",
        furnitureMaterialOptions: OFFICE_TABLE_MATERIALS,
        axes: { length: true, width: true, height: true },
        dimensionsSummaryLabel: "Desk size (optional)",
        entirelyOptionalPanel: true,
      };
  }
}

export function formatFurnitureMaterialLabel(
  material: FurnitureMaterial,
): string {
  if (material === "mdf") return "MDF";
  return material.charAt(0).toUpperCase() + material.slice(1);
}

export function isFurnitureMaterial(
  value: unknown,
): value is FurnitureMaterial {
  return (
    typeof value === "string" &&
    (FURNITURE_MATERIALS as readonly string[]).includes(value)
  );
}

export function isTableTopMaterial(
  value: unknown,
): value is DiningTableTopMaterial {
  return (
    typeof value === "string" &&
    (DINING_TABLE_TOP_MATERIALS as readonly string[]).includes(value)
  );
}

function isOptionalFt(
  value: unknown,
  min: number,
  max: number,
): value is number {
  return (
    typeof value === "number" &&
    Number.isFinite(value) &&
    value >= min &&
    value <= max
  );
}

export function isOptionalLengthFt(value: unknown): value is number {
  return isOptionalFt(value, LENGTH_FT_MIN, LENGTH_FT_MAX);
}

export function isOptionalWidthFt(value: unknown): value is number {
  return isOptionalFt(value, WIDTH_FT_MIN, WIDTH_FT_MAX);
}

export function isOptionalHeightFt(value: unknown): value is number {
  return isOptionalFt(value, HEIGHT_FT_MIN, HEIGHT_FT_MAX);
}

/** Append optional dimension numbers onto attributes (never required). */
export function appendOptionalDimensionAttributes(
  target: ListingSpecAttributes,
  form: Pick<
    FurnitureSurfaceSpecFormState,
    "lengthFt" | "widthFt" | "heightFt"
  >,
): ListingSpecAttributes {
  const out: ListingSpecAttributes = { ...target };
  if (isOptionalLengthFt(form.lengthFt)) out.length_ft = form.lengthFt;
  if (isOptionalWidthFt(form.widthFt)) out.width_ft = form.widthFt;
  if (isOptionalHeightFt(form.heightFt)) out.height_ft = form.heightFt;
  return out;
}

export function validateOptionalDimensionAttributes(
  record: Record<string, unknown>,
): ListingSpecValidationIssue[] {
  const issues: ListingSpecValidationIssue[] = [];
  if (
    record.length_ft !== undefined &&
    record.length_ft !== null &&
    record.length_ft !== "" &&
    !isOptionalLengthFt(record.length_ft)
  ) {
    issues.push({
      key: "length_ft",
      code: "out_of_range",
      message: `Length must be between ${LENGTH_FT_MIN} and ${LENGTH_FT_MAX} ft.`,
    });
  }
  if (
    record.width_ft !== undefined &&
    record.width_ft !== null &&
    record.width_ft !== "" &&
    !isOptionalWidthFt(record.width_ft)
  ) {
    issues.push({
      key: "width_ft",
      code: "out_of_range",
      message: `Width must be between ${WIDTH_FT_MIN} and ${WIDTH_FT_MAX} ft.`,
    });
  }
  if (
    record.height_ft !== undefined &&
    record.height_ft !== null &&
    record.height_ft !== "" &&
    !isOptionalHeightFt(record.height_ft)
  ) {
    issues.push({
      key: "height_ft",
      code: "out_of_range",
      message: `Height must be between ${HEIGHT_FT_MIN} and ${HEIGHT_FT_MAX} ft.`,
    });
  }
  return issues;
}

function surfaceDisplayName(
  subcategoryId: FurnitureSurfaceSubcategoryId,
): string {
  switch (subcategoryId) {
    case "center-table":
      return "Center Table";
    case "office-table":
      return "Office Table";
    case "tv-console":
      return "TV Console";
    case "wardrobe":
      return "Wardrobe";
    case "dresser":
      return "Dresser";
    case "table":
    default:
      return "Table";
  }
}

function usesTableTopMaterial(
  subcategoryId: FurnitureSurfaceSubcategoryId,
): boolean {
  return subcategoryId === "table" || subcategoryId === "center-table";
}

function usesFurnitureMaterial(
  subcategoryId: FurnitureSurfaceSubcategoryId,
): boolean {
  return (
    subcategoryId === "tv-console" ||
    subcategoryId === "wardrobe" ||
    subcategoryId === "dresser" ||
    subcategoryId === "office-table"
  );
}

export function buildFurnitureSurfaceAttributesFromForm(
  subcategoryId: string,
  form: FurnitureSurfaceSpecFormState,
): ListingSpecAttributes {
  if (!isFurnitureSurfaceSubcategory(subcategoryId)) return {};

  const base: ListingSpecAttributes = {};
  if (
    usesTableTopMaterial(subcategoryId) &&
    isTableTopMaterial(form.tableTopMaterial)
  ) {
    base.table_top_material = form.tableTopMaterial;
  }
  if (
    usesFurnitureMaterial(subcategoryId) &&
    isFurnitureMaterial(form.furnitureMaterial)
  ) {
    base.furniture_material = form.furnitureMaterial;
  }

  return appendOptionalDimensionAttributes(base, form);
}

export function furnitureSurfaceFormFromAttributes(
  subcategoryId: string,
  attributes: unknown,
): FurnitureSurfaceSpecFormState {
  const base = defaultFurnitureSurfaceSpecFormState();
  const record = asListingAttributesRecord(attributes);
  if (!record) return base;
  return {
    tableTopMaterial: isTableTopMaterial(record.table_top_material)
      ? record.table_top_material
      : "",
    furnitureMaterial: isFurnitureMaterial(record.furniture_material)
      ? record.furniture_material
      : "",
    lengthFt: isOptionalLengthFt(record.length_ft) ? record.length_ft : "",
    widthFt: isOptionalWidthFt(record.width_ft) ? record.width_ft : "",
    heightFt: isOptionalHeightFt(record.height_ft) ? record.height_ft : "",
  };
}

export function validateStoredFurnitureSurfaceAttributes(
  subcategoryId: string,
  attributes: ListingSpecAttributes | Record<string, unknown>,
): ListingSpecValidationIssue[] {
  if (!isFurnitureSurfaceSubcategory(subcategoryId)) return [];
  const record = attributes as Record<string, unknown>;
  const issues: ListingSpecValidationIssue[] = [];

  if (usesTableTopMaterial(subcategoryId)) {
    if (!isTableTopMaterial(record.table_top_material)) {
      issues.push({
        key: "table_top_material",
        code: "required",
        message: "Table top material is required.",
      });
    }
  }

  if (
    subcategoryId === "tv-console" ||
    subcategoryId === "wardrobe" ||
    subcategoryId === "dresser"
  ) {
    if (!isFurnitureMaterial(record.furniture_material)) {
      issues.push({
        key: "furniture_material",
        code: "required",
        message: "Material is required.",
      });
    }
  }

  // office-table: zero required structured keys
  issues.push(...validateOptionalDimensionAttributes(record));
  return issues;
}

export function getFurnitureSurfaceSpecFormValidationMessage(
  subcategoryId: string,
  form: FurnitureSurfaceSpecFormState,
): string | undefined {
  if (!isFurnitureSurfaceSubcategory(subcategoryId)) return undefined;
  if (usesTableTopMaterial(subcategoryId) && !form.tableTopMaterial) {
    return "Table top material is required.";
  }
  if (
    (subcategoryId === "tv-console" ||
      subcategoryId === "wardrobe" ||
      subcategoryId === "dresser") &&
    !form.furnitureMaterial
  ) {
    return "Material is required.";
  }
  const attrs = buildFurnitureSurfaceAttributesFromForm(subcategoryId, form);
  return validateStoredFurnitureSurfaceAttributes(subcategoryId, attrs)[0]
    ?.message;
}

function lengthPrefix(record: Record<string, unknown>): string {
  if (isOptionalLengthFt(record.length_ft)) {
    return `${record.length_ft}ft`;
  }
  return "";
}

export function composeFurnitureSurfaceTitleFromAttributes(
  subcategoryId: string,
  attributes: ListingSpecAttributes | Record<string, unknown>,
  identity?: ListingTitleIdentity | null,
): string {
  if (!isFurnitureSurfaceSubcategory(subcategoryId)) return "";
  const record = attributes as Record<string, unknown>;
  const name = surfaceDisplayName(subcategoryId);
  const prefix = lengthPrefix(record);

  if (usesTableTopMaterial(subcategoryId)) {
    if (!isTableTopMaterial(record.table_top_material)) return "";
    const mat = listingTitleMaterialWord(
      record.table_top_material,
      formatDiningTableTopMaterialLabel,
    );
    return composeListingTitleFromSlotsWithIdentity(
      {
        capacity: prefix || undefined,
        form: mat || undefined,
        product: name,
      },
      identity,
    );
  }

  if (subcategoryId === "office-table") {
    if (isFurnitureMaterial(record.furniture_material)) {
      const mat = listingTitleMaterialWord(
        record.furniture_material,
        formatFurnitureMaterialLabel,
      );
      return composeListingTitleFromSlotsWithIdentity(
        {
          capacity: prefix || undefined,
          form: mat || undefined,
          product: name,
        },
        identity,
      );
    }
    return composeListingTitleFromSlotsWithIdentity(
      {
        capacity: prefix || undefined,
        product: name,
      },
      identity,
    );
  }

  if (!isFurnitureMaterial(record.furniture_material)) return "";
  const mat = listingTitleMaterialWord(
    record.furniture_material,
    formatFurnitureMaterialLabel,
  );
  return composeListingTitleFromSlotsWithIdentity(
    {
      capacity: prefix || undefined,
      form: mat || undefined,
      product: name,
    },
    identity,
  );
}

export type WardrobeDresserInference = "wardrobe" | "dresser" | "ambiguous";

/**
 * Title inference for legacy `wardrobe-dresser` rows (D11) and sheet title-split.
 * Typos wadrobe/wardobe count as wardrobe.
 *
 * Ops policy (2026-08-25): bedroom-set / equal combo titles → **wardrobe** as
 * primary piece (sellers list one primary; comps follow). Mirror+console/table
 * without wardrobe → **dresser** (vanity/dressing). Dresser-as-accessory on a
 * wardrobe title → **wardrobe**.
 */
export function inferWardrobeOrDresserFromTitle(
  title: string,
): WardrobeDresserInference {
  const t = title.toLowerCase();

  // Dressing table / vanity console — not a wardrobe.
  if (
    !/\bwardrobes?\b/.test(t) &&
    !/\bwadrobes?\b/.test(t) &&
    !/\bwardobes?\b/.test(t) &&
    !/\bclosets?\b/.test(t) &&
    /\bmirror\b/.test(t) &&
    (/\bconsoles?\b/.test(t) || /\btables?\b/.test(t) || /\bvanitys?\b/.test(t))
  ) {
    return "dresser";
  }

  const hasDresser =
    /\bdressers?\b/.test(t) ||
    /\bvanitys?\b/.test(t) ||
    /\bvanities\b/.test(t) ||
    /chest of drawers/.test(t) ||
    (/\bdrawers?\b/.test(t) &&
      !/\bwardrobes?\b/.test(t) &&
      !/\bclosets?\b/.test(t));
  const hasWardrobe =
    /\bwardrobes?\b/.test(t) ||
    /\bwadrobes?\b/.test(t) ||
    /\bwardobes?\b/.test(t) ||
    /\bclosets?\b/.test(t) ||
    /\b[34]\s*-?\s*door\b/.test(t);

  if (hasDresser && !hasWardrobe) return "dresser";
  if (hasWardrobe && !hasDresser) return "wardrobe";

  if (hasWardrobe && hasDresser) {
    // Accessory dresser drawer on a wardrobe listing.
    if (/\bdresser\s+drawers?\b/.test(t)) return "wardrobe";
    // Equal combo / bedroom set → primary piece.
    return "wardrobe";
  }

  return "ambiguous";
}

/** Sheet `type` labels for WA extract / curator paste (Furniture category). */
export type WardrobeDresserSheetType =
  | "Wardrobes"
  | "Dressers & Vanities"
  | "Wardrobes & Dressers";

/**
 * Prefer split sheet types so sync does not land on combined `Wardrobes & Dressers`
 * unless inference is still ambiguous (neither signal).
 */
export function sheetTypeForWardrobeOrDresserTitle(
  title: string,
): WardrobeDresserSheetType {
  const inference = inferWardrobeOrDresserFromTitle(title);
  if (inference === "wardrobe") return "Wardrobes";
  if (inference === "dresser") return "Dressers & Vanities";
  return "Wardrobes & Dressers";
}
